// src/services/apiClient.js
import axios from 'axios'
import { toast } from 'react-hot-toast'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.logiqlink.com/api/v1',
  timeout: 30000, // Increased from 15000 to 30000 for file uploads
  headers: {
    'Content-Type': 'application/json',
    "ngrok-skip-browser-warning": "true"
  },
})

// Store a reference to the navigate function
let navigateRef = null
let isRefreshing = false

// Function to set navigate from outside
export const setNavigate = (navigate) => {
  navigateRef = navigate
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Don't set Content-Type header for FormData (let browser set it with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Check for success response structure
    if (response.data && typeof response.data.success !== 'undefined') {
      if (!response.data.success && response.data.message) {
        toast.error(response.data.message)
        return Promise.reject(new Error(response.data.message))
      }
    }
    return response.data
  },
  (error) => {
    const { response } = error

    // Don't show toast for 401 if we're already handling logout
    if (response?.status === 401 && !isRefreshing) {
      isRefreshing = true

      // Clear auth data
      localStorage.removeItem('token')
      localStorage.removeItem('user')

      // Only show toast and redirect if not on login page
      const isLoginPage = window.location.pathname.includes('/admin/login')
      if (!isLoginPage) {
        toast.error('Session expired. Please login again.')

        if (navigateRef) {
          navigateRef('/admin/login', {
            state: { from: window.location.pathname },
            replace: true
          })
        } else {
          window.location.href = '/admin/login'
        }
      }

      isRefreshing = false
    }

    // Handle 403 Forbidden
    if (response?.status === 403) {
      toast.error('Access denied. Insufficient permissions.')
    }

    // Handle 400 Bad Request
    if (response?.status === 400 && response.data?.message) {
      toast.error(response.data.message)
    }

    // Handle 500 Server Error
    if (response?.status >= 500) {
      toast.error('Server error. Please try again later.')
    }

    // For network errors
    if (!response) {
      toast.error('Network error. Please check your connection.')
    }

    return Promise.reject(error.response?.data || error)
  }
)

export default apiClient